import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { SortOrder } from 'src/services/common.types';
import { SortConfig, PageOptions } from 'src/services/api.types';
import { ContactLinkEntityType } from 'src/services/contacts/contacts.types';
import { useRemoveContactLinkMutation } from 'src/services/contacts/contacts.service';
import { ICompanyContactLinkListItem } from 'src/services/connections/connections.types';
import { useGetCompanyContactLinksQuery } from 'src/services/connections/connections.service';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import { RecordPanelMode } from 'src/components/record-panel/record-panel.types';
import RecordPanelFrame from 'src/components/record-panel/record-panel-frame';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import ContactRecordView from 'src/components/record-panel/views/contact-record-view';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';
import { resolveRecordNavigation } from 'src/components/record-panel/record-panel.utils';

import AddContactModal from 'src/sections/contacts/modal/add-contact-modal';
import ContactsDetailView from 'src/sections/contacts/view/contacts-detail-view';

import ContactLinkRowActions from './contact-link-row-actions';
import { buildContactLinkColumns } from './contact-link-columns';
import { ContactEditorDialog } from '../../modal/contact-editor-dialog';

const PAGE_SIZE = 50;

type Props = {
  profileId: string;
};

/**
 * A company's linked contacts on the shared DataList, replacing the hand-rolled table.
 *
 * Reuses the contacts column registry (see buildContactLinkColumns) so this tab and the main
 * contacts list stay in step, and gains what the old table lacked: configurable columns, per-column
 * search, server sort and infinite scroll. Its own preference key keeps the leaner default set here
 * from overwriting the user's contacts-list layout.
 *
 * Default sort is the link type, which floats the company's PRIMARY contact to the top as before.
 */
export default function ContactLinksList({ profileId }: Props) {
  const { t } = useTranslate();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  /**
   * The opened contact, in LOCAL state rather than the URL record-panel singleton (JT1-1725).
   *
   * This table renders inside MyCompaniesDetailView, which is itself what `RecordPanel type="company"`
   * renders in overlay mode. Writing `?preview_type=contact` would flip that panel's `isOpen` to false
   * and unmount the company detail — and this table, and any panel inside it — so the company overlay
   * would simply vanish. Same escape hatch as the org-chart peek: local state plus a bare frame pinned
   * above the host dialog.
   */
  const [preview, setPreview] = useState<{ id: string; mode: RecordPanelMode } | null>(null);

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<ICompanyContactLinkListItem[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'link_type', sort_order: SortOrder.ASC });

  const pageOptions: PageOptions = useMemo(() => ({ page, per_page: PAGE_SIZE }), [page]);

  const { currentData, isFetching } = useGetCompanyContactLinksQuery({
    profile_id: profileId,
    params: {
      ...pageOptions,
      search_query: filterQuery || undefined,
      sort_by: sortConfig.sort_by,
      sort_order: sortConfig.sort_order,
      column_filters: columnSearch,
    },
  });

  useEffect(() => {
    if (!currentData) return;
    setRows((prev) => {
      if (page === 1) return currentData.results;
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...currentData.results.filter((r) => !seen.has(r.id))];
    });
  }, [currentData, page]);

  const count = currentData?.count ?? 0;
  const hasMore = rows.length < count;
  const isSearching = isFetching || searchInput !== filterQuery;

  const resetToFirstPage = useCallback(() => setPage(1), []);

  const applySearch = useMemo(
    () => debounce((value: string) => { setFilterQuery(value); resetToFirstPage(); }, 300),
    [resetToFirstPage]
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const onSearchChange = useCallback((value: string) => { setSearchInput(value); applySearch(value); }, [applySearch]);
  const onSortChange = useCallback((next: SortConfig) => { setSortConfig(next); resetToFirstPage(); }, [resetToFirstPage]);
  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    resetToFirstPage();
  }, [resetToFirstPage]);
  const onLoadMore = useCallback(() => { if (!isFetching && hasMore) setPage((p) => p + 1); }, [isFetching, hasMore]);

  const contactEditor = useBoolean(false);
  const addContact = useBoolean();

  const [removeContactLink, { isLoading: isUnlinking }] = useRemoveContactLinkMutation();

  const handleUnlink = useCallback(async (row: ICompanyContactLinkListItem) => {
    try {
      // The LINK id, not the contact id — unlinking by contact would be a different, destructive act.
      await removeContactLink({
        link_id: row.link_id,
        entity_id: profileId,
        entity_type: ContactLinkEntityType.COMPANY_PROFILE,
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.remove_contact_link.success'), { variant: 'success' });
      setRows((prev) => prev.filter((r) => r.link_id !== row.link_id));
    } catch {
      enqueueSnackbar(t('companies.profile.api.remove_contact_link.default_error'), { variant: 'error' });
    }
  }, [profileId, removeContactLink, t]);

  const onNewContactAdded = useCallback(() => {
    contactEditor.onFalse();
    addContact.onFalse();
    resetToFirstPage();
    dispatch(rootApi.util.invalidateTags([
      { type: 'CompanyContactLink', id: profileId },
      { type: 'CompanyContacts', id: profileId },
    ]));
  }, [addContact, contactEditor, dispatch, profileId, resetToFirstPage]);

  const handleQuickView = useCallback((id: string) => setPreview({ id, mode: 'quick' }), []);
  const handleOpenOverlay = useCallback((id: string) => setPreview({ id, mode: 'overlay' }), []);

  // Prev/next walk the loaded rows; the indicator counts against the server total, as the URL panel does.
  const { prevId, nextId, position } = useMemo(
    () => resolveRecordNavigation(rows.map((r) => r.id), preview?.id, count),
    [rows, preview?.id, count]
  );

  const stepTo = useCallback(
    (id: string) => setPreview((prev) => (prev ? { ...prev, id } : prev)),
    []
  );

  const registry = useMemo(
    () => buildContactLinkColumns({ t, onOpenRecord: handleOpenOverlay }),
    [t, handleOpenOverlay]
  );
  const { columns, pickerModel, save } = useColumnPreferences('company_contacts', registry);

  const renderRowActions = useCallback((row: ICompanyContactLinkListItem) => (
    <ContactLinkRowActions row={row} onView={handleQuickView} onUnlink={handleUnlink} isUnlinking={isUnlinking} />
  ), [handleQuickView, handleUnlink, isUnlinking]);

  const renderRowLeading = useCallback((row: ICompanyContactLinkListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const toolbar = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <TextField
        size="small"
        value={searchInput}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('common.placeholders.search')}
        InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
        sx={{ width: 260 }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <DataListColumnPicker model={pickerModel} onSave={save} />
        <Button
          size="medium"
          variant="contained"
          endIcon={<Iconify icon="material-symbols:add" />}
          onClick={contactEditor.onTrue}
        >
          {t('companies.profile.linked_contacts.link_contact')}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <>
      <Card>
        <DataList<ICompanyContactLinkListItem>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isSearching={isSearching}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          sort={sortConfig}
          onSortChange={onSortChange}
          columnSearch={columnSearch}
          onColumnSearchChange={onColumnSearchChange}
          toolbar={toolbar}
          renderRowLeading={renderRowLeading}
          renderRowActions={renderRowActions}
        />
      </Card>

      {/* Pinned to zIndex.modal so it sits on TOP of a company overlay opened earlier at the same
          layer, rather than depending on which dialog portalled last. */}
      <RecordPanelFrame
        variant={preview?.mode ?? 'quick'}
        open={!!preview}
        onClose={() => setPreview(null)}
        onPrev={prevId ? () => stepTo(prevId) : undefined}
        onNext={nextId ? () => stepTo(nextId) : undefined}
        position={position}
        zIndex={theme.zIndex.modal}
      >
        {preview && (preview.mode === 'overlay'
          ? <ContactsDetailView overrideContactId={preview.id} />
          : <ContactRecordView id={preview.id} />)}
      </RecordPanelFrame>

      {contactEditor.value && (
        <ContactEditorDialog
          open={contactEditor.value}
          onClose={contactEditor.onFalse}
          profileId={profileId}
          onAddContact={addContact.onTrue}
        />
      )}

      <AddContactModal
        open={addContact.value}
        onClose={addContact.onFalse}
        companyId={profileId}
        onSuccess={onNewContactAdded}
      />
    </>
  );
}
